import React, { useEffect, useRef, useState } from "react";

const MARGIN = 16;
const HEADER_HEIGHT = 42;

const initialFrame = () => {
  const width = Math.min(900, window.innerWidth - MARGIN * 2);
  const height = Math.min(Math.round(window.innerHeight * 0.85), window.innerHeight - MARGIN * 2);
  return { x: Math.max(MARGIN, (window.innerWidth - width) / 2), y: MARGIN * 2, width, height };
};

// Floating, draggable document viewer -- no dimmed backdrop, so the rest of
// the playground stays visible and usable while the file is open. Browsers
// render both PDFs and images natively inside an <iframe> from the
// response's Content-Type, so this needs no PDF.js/viewer dependency.
const PdfPreviewModal = ({ open, onClose, title, url }) => {
  const [frame, setFrame] = useState(null);
  const [dragging, setDragging] = useState(false);
  const dragOffset = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    if (open) setFrame((current) => current || initialFrame());
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!dragging) return undefined;

    // Keeps the title bar on screen so the window can always be grabbed again.
    const onMove = (e) => {
      const { dx, dy } = dragOffset.current;
      setFrame((f) => ({
        ...f,
        x: Math.min(Math.max(e.clientX - dx, MARGIN - f.width + 120), window.innerWidth - 120),
        y: Math.min(Math.max(e.clientY - dy, 0), window.innerHeight - HEADER_HEIGHT),
      }));
    };
    const onUp = () => setDragging(false);

    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
    return () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
    };
  }, [dragging]);

  if (!open || !frame) return null;

  // The browser's native resize handle changes the box's size; keep frame in
  // sync so a later drag doesn't snap it back.
  const syncSize = () => {
    const box = boxRef.current;
    if (box && (box.offsetWidth !== frame.width || box.offsetHeight !== frame.height)) {
      setFrame((f) => ({ ...f, width: box.offsetWidth, height: box.offsetHeight }));
    }
  };

  const startDrag = (e) => {
    if (e.button !== 0 || e.target.closest("button")) return;
    e.preventDefault();
    syncSize();
    dragOffset.current = { dx: e.clientX - frame.x, dy: e.clientY - frame.y };
    setDragging(true);
  };

  return (
    <div
      ref={boxRef}
      role="dialog"
      aria-label={title}
      onPointerUp={syncSize}
      style={{
        position: "fixed",
        left: frame.x,
        top: frame.y,
        width: frame.width,
        height: frame.height,
        minWidth: "280px",
        minHeight: "200px",
        zIndex: 1050,
        background: "#fff",
        borderRadius: "6px",
        boxShadow: "0 8px 30px rgba(0, 0, 0, 0.3)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        resize: "both",
      }}
    >
      <div
        className="d-flex justify-content-between align-items-center px-3 border-bottom"
        style={{ height: HEADER_HEIGHT, flexShrink: 0, cursor: dragging ? "grabbing" : "grab", userSelect: "none", touchAction: "none" }}
        onPointerDown={startDrag}
      >
        <h6 className="mb-0 text-truncate" title={title}>
          {title}
        </h6>
        <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
      </div>
      <div style={{ position: "relative", flex: 1 }}>
        {url && (
          <iframe
            src={url}
            title={title}
            style={{ width: "100%", height: "100%", border: "none", display: "block" }}
          />
        )}
        {/* The iframe would swallow pointer events mid-drag; cover it while dragging. */}
        {dragging && <div style={{ position: "absolute", inset: 0 }} />}
      </div>
    </div>
  );
};

export default PdfPreviewModal;
